/*----------------------------------
- LANDING: PRODUCT APPRAISE PAGE CONTENT (SSOT)
----------------------------------*/

// All copy for /product/appraise. Provider identity (label, price, batch size,
// live/soon) is NOT restated here: it comes from the appraisal provider catalog in
// @uniquedomains/core, so a price change or a newly wired source updates the page
// without a copy edit. Only the editorial angle per provider lives here.

import type { AppraisalProviderId } from '@uniquedomains/core/domains/appraisal';

import { AVAILABILITY_RUN_SIZE } from '@uniquedomains/components/contracts/lookup/limits';
import { START_WITH_BRIEF_LABEL } from '@/client/catalogs/landing/accessOffer';
import type { FAQItem } from '@/client/catalogs/landing/faq';

/*----------------------------------
- TYPES
----------------------------------*/

/**
 * One appraisal source, as the page argues about it.
 *
 * The angle grid and the comparison table describe the same four sources from two
 * angles, so they read one list: a source added to the catalog cannot appear in one
 * section and be forgotten in the other.
 */
export type ProductAppraiseSource = {
    id: AppraisalProviderId;
    angle: string;
    weighs: string;
    misses: string;
    returns: string;
    judgesOn: string;
    bestFor: string;
};

export type ProductAppraiseStep = {
    step: string;
    title: string;
    body: string;
};

export type ProductAppraiseGlossaryTerm = {
    term: string;
    definition: string;
};

/*----------------------------------
- CONTENT
----------------------------------*/

export const PRODUCT_APPRAISE_PAGE_CONTENT = {
    hero: {
        titleLead: 'Appraise a domain',
        titleTrail: 'with every source at once',
        body: 'Every appraiser has a blind spot. Run your names through all of them at once and read a realistic range instead of one tool’s number.',
    },

    /**
     * The four figures under the hero. Only their labels live here: the values are
     * read from the provider catalog by the page, so a newly wired source or a
     * changed batch size moves the band without a copy edit.
     */
    inShort: {
        eyebrow: 'In short',
        factLabels: {
            sources: 'Independent appraisal sources, one live today',
            axis: 'Shared axis every answer is plotted on',
            channels: 'Values HumbleWorth alone returns per name',
            batch: 'Domains per HumbleWorth request, batched for you',
        },
    },

    consensus: {
        title: 'One name, every source at once',
        lead: 'Unique Domains sends every name to several independent appraisers in one pass and plots their answers on a single axis. Only HumbleWorth returns a range, covering auction, marketplace and brokerage, while the others return one figure each. Where they overlap is the estimate you can explain.',
        // Illustrative figures. No real catalog row is shown on a public surface.
        sampleName: 'pique.com',
        sampleStatus: 'expiring',
        rangeChipLabel: 'Realistic range',
        cardBody: 'The range model puts this name at $24.3k; the registrar’s own basis says $17.4k. Neither is wrong, they are answering slightly different questions. The range where they overlap is the number you can stand behind in a negotiation.',
        illustrativeNote: 'Illustrative example with sample figures, not a live listing. The second opinion is drawn to show how two sources read together; only HumbleWorth runs today.',
    },

    angles: {
        title: 'Every appraiser is looking at something different',
        body: 'Each appraiser was built on a different slice of the market and rewards different qualities in a name, so each is confident in a different place and blind in a different place. One number hides which of those you are looking at.',
        /** Follows the source count on the phone, where the cards are a swipe row. */
        swipeHint: 'sources · swipe',
        weighsLabel: 'Weighs',
        missesLabel: 'Misses',
    },

    sources: [
        {
            id: 'humbleworth',
            angle: 'A model trained on what domains actually sold for, across the whole market.',
            weighs: 'Realised sale prices at scale, name shape, length, extension.',
            misses: 'Very recent market shifts, trademark exposure, one specific buyer’s need.',
            returns: 'Auction, marketplace and broker-negotiated price',
            judgesOn: 'Historical sale prices, market-wide',
            bestFor: 'A range you can stand behind on any name',
        },
        {
            id: 'godaddy',
            angle: 'A registrar’s view, anchored in the transactions it can see first-hand.',
            weighs: 'Its own sales basis, keyword value, extension strength.',
            misses: 'Sales that happened off its platform, private brokerage outcomes.',
            returns: 'A single point estimate',
            judgesOn: 'Its own registrar transactions',
            bestFor: 'Names likely to sell through GoDaddy',
        },
        {
            id: 'estibot',
            angle: 'Keyword economics: what advertisers already pay for the words in the name.',
            weighs: 'Search volume, cost per click, exact-match commercial intent.',
            misses: 'Brandability, sound, invented names with no search history.',
            returns: 'A single point estimate',
            judgesOn: 'Keyword economics and search demand',
            bestFor: 'Generic, high-intent keywords',
        },
        {
            id: 'domainindex',
            angle: 'Index position: where the name ranks among similar names.',
            weighs: 'How quickly it sells, TLD strength, length class, category depth.',
            misses: 'Story value, category timing, a founder’s emotional premium.',
            returns: 'Value plus how quickly it sells',
            judgesOn: 'Index position across TLDs',
            bestFor: 'Comparing liquidity across a list',
        },
    ] as const satisfies readonly ProductAppraiseSource[],

    steps: {
        title: 'How an appraisal run works',
        items: [
            {
                step: '1',
                title: 'Bring the names',
                body: 'Paste a list, upload a CSV and map the domain column, or pull the saved names of a Watchlist or a Radar. Names do not have to exist in the Unique Domains database.',
            },
            {
                step: '2',
                title: 'Choose the sources',
                body: 'One source gives you a number. Two or more is the point of the tool: their disagreement becomes a sortable column. Only HumbleWorth answers with a range, the others return one figure each.',
            },
            {
                step: '3',
                title: 'Run once',
                body: 'Your list is split into the batch size each source accepts and sent for you. HumbleWorth takes 100 names per request. Names appraised before come straight back from cache without a call.',
            },
            {
                step: '4',
                title: 'Read them together',
                body: 'Each row draws the floor-to-ceiling range on one shared scale, with every other source pinned onto that same scale, so agreement and disagreement are visible at a glance.',
            },
        ] as const satisfies readonly ProductAppraiseStep[],
    },

    comparison: {
        title: 'Appraisal sources compared',
        body: 'No single source is authoritative, and they do not answer in the same shape. Running them on the same list is how you tell a confident number from a contested one.',
        headers: {
            source: 'Source',
            returns: 'Returns',
            judgesOn: 'Judges on',
            bestFor: 'Best for',
            status: 'Status',
        },
        ctaLabel: 'Compare plans',
    },

    faq: {
        title: 'Questions investors ask',
        body: 'Everything the appraisal actually promises, and everything it does not.',
        ctaLabel: 'Contact me',
        // Every question here is also emitted as FAQPage structured data. The two
        // lists must stay identical: markup for an answer that is not on the page
        // is a structured-data violation.
        items: [
            {
                question: 'How is a domain appraised on Unique Domains?',
                answer: 'Each name is sent to HumbleWorth’s price-predict-v1 model, run through Replicate. The model returns three figures per domain: an auction value, a marketplace value and a broker-negotiated price. Further sources can be added to compare independent opinions on the same name.',
            },
            {
                question: 'Is a domain appraisal a guaranteed price?',
                // The sales archive is public at /sales since ADR-0067: the answer says the model does
                // not read it, rather than claiming no sales data exists in the product.
                answer: "No. Every figure is a directional AI estimate trained on historical sales. It is not an offer and not a formal appraisal. The appraisal does not read the sales archive: the figure is the model's own. What names actually sold for is on the sales page. Treat it as an anchor for your own judgement.",
            },
            {
                question: 'What is the difference between auction, marketplace and broker-negotiated price?',
                answer: 'Auction is the lowest quick-sale price, what a name plausibly fetches in a seven-day expiry auction. Marketplace is what a listed name plausibly closes at given patience. Brokerage is a brokered outcome to a matched end buyer: rare, slow, and the top of the range.',
            },
            {
                question: 'Can I appraise domains I do not own?',
                answer: 'Yes. The model takes plain domain strings, so a name does not need to exist in the Unique Domains database, in a Watchlist, or even be registered.',
            },
            {
                question: 'Why do two sources disagree on the same name?',
                answer: 'Because they are answering slightly different questions. One is trained on realised sale prices market-wide, another on a single registrar’s transactions, another on keyword demand. A tight agreement is a number you can stand behind; a wide gap usually means the name is unusual enough that no single method has much to go on.',
            },
            {
                question: 'Which appraisal sources return a range rather than one number?',
                answer: 'Only HumbleWorth. Its price-predict-v1 model returns an auction, a marketplace and a broker-negotiated price for every name. GoDaddy, Estibot and DomainIndex each return a single point estimate, so on a shared axis HumbleWorth draws a bar and the others land as single points.',
            },
            {
                question: 'How many domains can be appraised at once?',
                answer: 'Each source has its own batch size and Unique Domains splits the run to match it, with HumbleWorth accepting 100 domains per request. A 500-name list therefore becomes 5 HumbleWorth requests, handled for you. Your plan sets how many names one run can carry.',
            },
            {
                question: 'Does re-running the same name cost again?',
                answer: 'No. A successful estimate is immutable for a given model version, so re-running the same name returns the stored figure at no cost. A new model version creates a new appraisal.',
            },
            {
                question: 'Do you sell or share the names I paste?',
                answer: 'No. Your list is yours. Appraisal requests carry the domain strings only, with no account or list context attached.',
            },
        ] as const satisfies readonly FAQItem[],
    },

    glossary: {
        title: 'Terms on this page',
        items: [
            { term: 'Domain appraisal', definition: 'An estimate of what a domain name would sell for, produced by a model or an analyst rather than by an actual sale.' },
            { term: 'Auction value', definition: 'The price a name plausibly reaches in an expiry auction, where the buyers are mostly other investors.' },
            { term: 'Marketplace value', definition: 'The price a listed name plausibly closes at when an end buyer eventually arrives.' },
            { term: 'Broker-negotiated price', definition: 'The price a broker could negotiate with a matched end buyer, typically the top of the range.' },
            { term: 'Price gap', definition: 'The distance between the floor and the ceiling estimate for one name. A wide gap signals a hard-to-sell, judgement-heavy name.' },
            { term: 'Lowest quick-sale price', definition: 'The figure you could realistically convert to cash quickly, which for most domains is the auction value.' },
            { term: 'Range model', definition: 'An appraiser that answers with several figures for one name instead of a single price. HumbleWorth is the only range model wired in.' },
            { term: 'Point estimate', definition: 'A single-figure appraisal with no stated range around it. GoDaddy, Estibot and DomainIndex all answer this way.' },
            { term: 'Multi-source appraisal', definition: 'Valuing one name with several independent methods and reading them together, rather than trusting a single tool’s output.' },
            { term: 'Realistic range', definition: 'The range where independent appraisal methods overlap. Narrower than any single tool’s range and easier to stand behind in a negotiation.' },
        ] as const satisfies readonly ProductAppraiseGlossaryTerm[],
    },

    finalCta: {
        title: 'Stop trusting one number.',
        // The run size is the checker's, which `limits.ts` documents as Appraise's sizes.
        body: `Your list is already in the box. With Full Access every source runs at once, up to ${AVAILABILITY_RUN_SIZE.fullAccess} names a run.`,
        ctaLabel: START_WITH_BRIEF_LABEL,
    },

    /**
     * Phone action bar, primary slot. The secondary slot keeps the site default
     * ("Log in"), which the mockup's bar also shows.
     */
    mobileActionLabel: 'Run the appraisal',
} as const;
